import { useCallback, useEffect, useState } from 'react'
import {
  AlertCircle,
  Bell,
  Crosshair,
  FileText,
  RefreshCw,
  ScrollText,
  ShieldCheck,
} from 'lucide-react'
import { getCases, getCaseLifecycle } from '../lib/api'
import { AnalystNavigation } from '../components/Navigation'
import { EmptyState } from '../components/EmptyState'
import { InvestigationDetail } from '../components/InvestigationDetail'
import { SeverityBadge } from '../components/SeverityBadge'
import { StatusBadge } from '../components/StatusBadge'
import { cn, formatDate, timeAgo } from '../lib/utils'
import type { Case, CaseLifecycle, LifecycleSignal } from '../types'

/**
 * Human-analyst escalation view (TASKS/analyst-escalation-view.md).
 *
 * Everything the AI SOC has escalated, newest first, with FULL technical
 * detail per case: the Concierge narrative, the L1/L2/L3 investigation
 * (every tool call + result + deciding tier), patterns-of-life context, and
 * canary correlation where present.
 *
 * READ-ONLY by design: this slice renders what happened; it can execute
 * nothing. No approve/dismiss/action controls (the approval path is
 * deliberately inert — see fix-concierge-approval-honesty). Real data or an
 * honest empty state — never fabricated incidents (§7).
 */

function CanaryFirePanel({ signal }: { signal: LifecycleSignal }) {
  const ctx = signal.context ?? {}
  const rows = ([
    ['Decoy (memo)', ctx.memo],
    ['Token type', ctx.token_type],
    ['Fired at', signal.occurred_at],
    ['Source IP', ctx.trigger_source_ip],
    ['User agent', ctx.trigger_user_agent],
    ['Channel', ctx.trigger_channel],
  ] as Array<[string, unknown]>).filter(([, v]) => v != null && v !== '')

  return (
    <div className="rounded-lg border border-danger/40 bg-danger-light/60 p-3">
      <p className="text-[11px] font-bold uppercase tracking-wide text-danger flex items-center gap-1.5 mb-1.5">
        <Crosshair className="w-3.5 h-3.5" aria-hidden="true" />
        Canary tripwire fired
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-gray-500">{label}</dt>
            <dd className="text-navy font-medium break-words">{String(value)}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-1.5 text-[11px] text-gray-600">
        The provider reported an interaction with a planted decoy. The analyst
        assesses what happened using the client’s history; the trigger alone
        does not establish who accessed it or why.
      </p>
    </div>
  )
}

function SignalRow({ signal }: { signal: LifecycleSignal }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded border border-gray-200 bg-white px-2.5 py-1.5">
      <button
        type="button"
        className="w-full flex items-center gap-2 text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <code className="text-xs font-semibold text-navy">{signal.signal_type}</code>
        <span className="text-[11px] text-gray-500">
          severity {signal.severity ?? 'unassessed'} · confidence {signal.confidence == null ? 'unassessed' : `${Math.round(signal.confidence * 100)}%`}
        </span>
        <span className="ml-auto text-[11px] text-gray-400 whitespace-nowrap">
          {signal.occurred_at ? formatDate(signal.occurred_at) : 'time unknown'}
        </span>
      </button>
      {open && (
        <pre className="mt-1.5 text-[11px] leading-4 text-gray-700 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
          {JSON.stringify(signal.context, null, 1)}
        </pre>
      )}
    </div>
  )
}

function CaseDetailPane({ caseItem }: { caseItem: Case }) {
  const [lifecycle, setLifecycle] = useState<CaseLifecycle | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setLifecycle(null)
    getCaseLifecycle(caseItem.id)
      .then((data) => {
        if (!cancelled) setLifecycle(data)
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the incident lifecycle from the backend.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [caseItem.id])

  const narrative = lifecycle?.case.narrative ?? caseItem.narrative
  const canarySignals =
    lifecycle?.signals.filter((s) => s.signal_type.startsWith('canary_token')) ?? []
  const otherSignals =
    lifecycle?.signals.filter((s) => !s.signal_type.startsWith('canary_token')) ?? []

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-mono text-xs text-gray-500">{caseItem.case_number}</span>
          <SeverityBadge severity={caseItem.severity} size="sm" />
          <StatusBadge status={caseItem.status} size="sm" />
          <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[11px] font-medium">
            Tier {caseItem.autonomy_tier}
          </span>
          <span className="ml-auto text-[11px] text-gray-400">
            {formatDate(caseItem.created_at)} · {timeAgo(caseItem.created_at)}
          </span>
        </div>
        <h2 className="text-lg font-serif font-bold text-navy leading-snug">
          {caseItem.title}
        </h2>
        <p className="text-[11px] text-gray-500 mt-0.5">
          principal <code>{caseItem.principal_id}</code>
        </p>
        {caseItem.description && (
          <p className="mt-1 text-xs text-gray-600 leading-5">{caseItem.description}</p>
        )}
      </div>

      {/* Concierge narrative */}
      <section aria-label="Concierge narrative">
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5 flex items-center gap-1.5">
          <ScrollText className="w-3.5 h-3.5 text-brass" aria-hidden="true" />
          Concierge narrative (what the client is told)
        </h3>
        {narrative ? (
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-sm leading-6 text-navy whitespace-pre-wrap font-serif">
              {narrative}
            </p>
          </div>
        ) : (
          <p className="text-xs text-gray-500 italic">
            No narrative was generated for this case (generation is fail-soft —
            absence means it failed or predates the feature, not that one is hidden).
          </p>
        )}
      </section>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-gray-500 py-4">
          <div className="w-4 h-4 border-2 border-brass border-t-transparent rounded-full animate-spin" />
          Loading incident lifecycle…
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-light p-3 text-xs text-danger">
          <AlertCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
          {error}
        </div>
      )}

      {lifecycle && (
        <>
          {/* Canary fire context */}
          {canarySignals.map((s) => (
            <CanaryFirePanel key={s.id} signal={s} />
          ))}

          {/* Investigation */}
          <section aria-label="Investigation detail">
            <h3 className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
              Investigation — tiers, tool calls, verdict
            </h3>
            <InvestigationDetail investigation={lifecycle.investigation} />
          </section>

          {/* Triggering signals */}
          <section aria-label="Triggering signals">
            <h3 className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
              Triggering signals ({lifecycle.signals.length})
            </h3>
            {lifecycle.signals.length === 0 ? (
              <p className="text-xs text-gray-500 italic">
                No signals are linked to this case in the database.
              </p>
            ) : (
              <div className="space-y-1.5">
                {canarySignals.concat(otherSignals).map((s) => (
                  <SignalRow key={s.id} signal={s} />
                ))}
              </div>
            )}
          </section>

          {/* Alerts sent */}
          <section aria-label="Alerts">
            <h3 className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5 flex items-center gap-1.5">
              <Bell className="w-3.5 h-3.5 text-brass" aria-hidden="true" />
              Alerts raised ({lifecycle.alerts.length})
            </h3>
            {lifecycle.alerts.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No alerts recorded for this case.</p>
            ) : (
              <div className="space-y-1.5">
                {lifecycle.alerts.map((a, i) => (
                  <div key={i} className="rounded border border-gray-200 bg-white px-2.5 py-1.5">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-medium text-navy flex-1">{a.headline}</p>
                      <StatusBadge status={a.status} size="sm" />
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      via {a.channel}
                      {a.created_at ? ` · ${formatDate(a.created_at)}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Audit trail */}
          {lifecycle.audit_trail.length > 0 && (
            <section aria-label="Audit trail">
              <h3 className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
                Audit trail
              </h3>
              <div className="rounded border border-gray-200 bg-white divide-y divide-gray-100">
                {lifecycle.audit_trail.map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 px-2.5 py-1 text-[11px]">
                    <code className="text-navy">{entry.action_type}</code>
                    <span className="text-gray-500">{entry.actor_type}</span>
                    <span className="ml-auto text-gray-400 whitespace-nowrap">
                      {entry.created_at ? formatDate(entry.created_at) : ''}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}

export function EscalationsPage() {
  const [cases, setCases] = useState<Case[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchEscalations = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getCases({ page_size: 100 })
      const sorted = [...response.items].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
      setCases(sorted)
      setSelectedId((prev) =>
        prev && sorted.some((c) => c.id === prev) ? prev : (sorted[0]?.id ?? null),
      )
    } catch {
      setError(
        'Could not reach the backend. Nothing is shown because nothing could be loaded — this view never invents incidents.',
      )
      setCases([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchEscalations()
  }, [fetchEscalations])

  const selected = cases.find((c) => c.id === selectedId) ?? null

  return (
    <div className="min-h-screen bg-cream">
      <AnalystNavigation />

      <main className="max-w-[1600px] mx-auto px-4 py-4">
        <div className="flex items-center gap-3 mb-3">
          <h1 className="text-lg font-serif font-bold text-navy">Escalations</h1>
          <p className="text-xs text-gray-500">
            Everything the team has escalated, newest first. Read-only — this
            surface executes nothing.
          </p>
          <button
            type="button"
            onClick={() => void fetchEscalations()}
            disabled={loading}
            className="ml-auto tap-min flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-xs text-navy hover:border-brass disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', loading && 'animate-spin')} aria-hidden="true" />
            Refresh
          </button>
        </div>

        {error ? (
          <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-light p-4 text-sm text-danger max-w-xl">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
            {error}
          </div>
        ) : loading && cases.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-brass border-t-transparent rounded-full animate-spin" />
          </div>
        ) : cases.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="Nothing escalated"
            message="The team has not escalated any cases for this tenant. Quiet is quiet — this view never fabricates incidents."
          />
        ) : (
          <div className="grid grid-cols-[minmax(300px,380px)_1fr] gap-4 items-start">
            {/* Queue */}
            <div
              role="list"
              aria-label="Escalated cases, newest first"
              className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100 max-h-[calc(100vh-140px)] overflow-y-auto"
            >
              {cases.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="listitem"
                  onClick={() => setSelectedId(c.id)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 hover:bg-brass/5 transition-colors',
                    c.id === selectedId && 'bg-brass/10 border-l-2 border-brass',
                  )}
                  aria-current={c.id === selectedId}
                >
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <SeverityBadge severity={c.severity} size="sm" />
                    <StatusBadge status={c.status} size="sm" />
                    <span className="ml-auto text-[10px] text-gray-400 whitespace-nowrap">
                      {timeAgo(c.created_at)}
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-navy leading-snug line-clamp-2">
                    {c.title}
                  </p>
                  <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                    {c.case_number} · T{c.autonomy_tier}
                  </p>
                </button>
              ))}
            </div>

            {/* Detail */}
            <div className="rounded-xl border border-gray-200 bg-cream/50 p-4 max-h-[calc(100vh-140px)] overflow-y-auto">
              {selected ? (
                <CaseDetailPane caseItem={selected} />
              ) : (
                <EmptyState
                  icon={FileText}
                  title="Select a case"
                  message="Pick a case from the queue to see its full detail."
                />
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
